import { FunctionComponent, useEffect, useState, useMemo } from 'react'
import DashboardLayout from '../../components/layout/layout'
import { Box, Button, Paper, Skeleton, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Tooltip } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetCompanyCustomerViewOrders, useGetCompanyCustomerCloseOrder, useGetCompanyCustomerOpenOrder } from '../../hooks/api/oms';
import moment from "moment";
import { useQueryClient } from '@tanstack/react-query';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

const TableCol = styled(TableCell)(({ theme }) => ({
  border: '1px solid rgba(224, 224, 224, 1)',
}));

type SortField = 'orderNumber' | 'customerPo' | 'orderDate';
type SortDirection = 'asc' | 'desc' | null;
type SortState = { field: SortField | null; direction: SortDirection };

const CheckOrderStatusCustomerOrderDetails: FunctionComponent = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { orderNumber } = useParams();



  const {data: customersForViewOrders, isFetched: hasCustomersFetched} = useGetCompanyCustomerViewOrders();
  const {data: customerOpenOrders, isFetched: hasOpenFetched, isLoading: isOpenLoading} = useGetCompanyCustomerOpenOrder(orderNumber, true);
  const {data: customerCloseOrders, isFetched: hasCloseFetched, isLoading: isCloseLoading} = useGetCompanyCustomerCloseOrder(orderNumber, true);

 
  if (customersForViewOrders && Array.isArray(customersForViewOrders)) {
    console.log('Available customers:');
    customersForViewOrders.forEach((customer, index) => {
      console.log(`  ${index}: custNum="${customer.custNum}" (type: ${typeof customer.custNum}), companyName="${customer.companyName}"`);
    });
    
    const foundCustomer = customersForViewOrders.find((customer: any) => customer.custNum === orderNumber);
    customersForViewOrders.forEach((customer) => {
      const isMatch = customer.custNum === orderNumber;
      const trimmedMatch = customer.custNum?.trim() === orderNumber?.trim();
      console.log(`  Trimmed comparison: "${customer.custNum?.trim()}" === "${orderNumber?.trim()}": ${trimmedMatch}`);
    });
  } else {
    console.log('customersForViewOrders is not an array or is undefined/null');
  }

  useEffect(() => {
    if (!orderNumber) {
      navigate('/order-management-system/check-order-status')
    }
  }, [navigate, orderNumber]);

  // Sorting state for open and closed tables
  const [openSort, setOpenSort] = useState<SortState>({ field: null, direction: null });
  const [closeSort, setCloseSort] = useState<SortState>({ field: null, direction: null });

  // Get all data from API responses
  const allOpenOrders = useMemo(() => {
    return Array.isArray(customerOpenOrders) ? customerOpenOrders : [];
  }, [customerOpenOrders]);

  const allCloseOrders = useMemo(() => {
    return Array.isArray(customerCloseOrders) ? customerCloseOrders : [];
  }, [customerCloseOrders]);





  // Sorting logic
  const sortedOpenOrders = useMemo(() => {
    // First, deduplicate the orders by orderNumber
    const uniqueOrders = allOpenOrders.filter((order: any, index: number, self: any[]) => 
      index === self.findIndex((o: any) => o.orderNumber === order.orderNumber)
    );
    
    if (!openSort.field || !openSort.direction) return uniqueOrders;
    
    return [...uniqueOrders].sort((a, b) => {
      let aValue: any = a[openSort.field!];
      let bValue: any = b[openSort.field!];
      if (openSort.field === 'orderDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }
      if (openSort.direction === 'asc') return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
  }, [allOpenOrders, openSort]);

  const sortedCloseOrders = useMemo(() => {
    // First, deduplicate the orders by orderNumber
    const uniqueOrders = allCloseOrders.filter((order: any, index: number, self: any[]) => 
      index === self.findIndex((o: any) => o.orderNumber === order.orderNumber)
    );
    
    
    if (!closeSort.field || !closeSort.direction) return uniqueOrders;
    
    return [...uniqueOrders].sort((a, b) => {
      let aValue: any = a[closeSort.field!];
      let bValue: any = b[closeSort.field!];
      if (closeSort.field === 'orderDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }
      if (closeSort.direction === 'asc') return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    });
  }, [allCloseOrders, closeSort]);

  // Use all sorted data (no pagination)
  const displayOpenOrders = useMemo(() => {
    return sortedOpenOrders;
  }, [sortedOpenOrders]);

  const displayCloseOrders = useMemo(() => {
    return sortedCloseOrders;
  }, [sortedCloseOrders]);



  // Sort handlers
  const handleOpenSort = (field: SortField) => {
    setOpenSort(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });

  };
  const handleCloseSort = (field: SortField) => {
    setCloseSort(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });

  };
  // Sort icon
  const getSortIcon = (sort: SortState, field: SortField) => {
    if (sort.field !== field) return <UnfoldMoreIcon fontSize="small" />;
    if (sort.direction === 'asc') return <ArrowUpwardIcon fontSize="small" />;
    if (sort.direction === 'desc') return <ArrowDownwardIcon fontSize="small" />;
    return <UnfoldMoreIcon fontSize="small" />;
  };

  return (
    <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Orders Management"} breadcrumbs={'Check Order Status'} activeMenu={'oms'}>
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
            Order History For: {hasCustomersFetched ? (customersForViewOrders?.find((customer: any) => customer.custNum?.trim() === orderNumber?.trim())?.companyName || 'Unknown Company') : 'Loading...'}
          </Typography>
        </Box>
        <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0 }}>
          <Table size="small" aria-label="inventory list table">
            <TableHead sx={{ backgroundColor: "#c3183c"}}>
              <TableRow>
                <TableCell colSpan={8}  sx={{ color: 'white' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Open Orders
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Results: {sortedOpenOrders.length}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
              <TableRow>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleOpenSort('orderNumber')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Sales Order #
                    <Tooltip title="Click to sort">{getSortIcon(openSort, 'orderNumber')}</Tooltip>
                  </Box>
                </TableCol>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleOpenSort('customerPo')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Purchase Order #
                    <Tooltip title="Click to sort">{getSortIcon(openSort, 'customerPo')}</Tooltip>
                  </Box>
                </TableCol>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleOpenSort('orderDate')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Order Date
                    <Tooltip title="Click to sort">{getSortIcon(openSort, 'orderDate')}</Tooltip>
                  </Box>
                </TableCol>
              </TableRow>
            </TableHead>
            <TableBody>
              {isOpenLoading ? (
                <TableRow>
                  <TableCol colSpan={3}>
                    <Skeleton width="38%" />
                    <Skeleton width="100%" />
                    <Skeleton width="100%" />
                    <Skeleton width="61%" />
                  </TableCol>
                </TableRow>
              ) : hasOpenFetched && displayOpenOrders.length === 0 ? (
                <TableRow>
                  <TableCol colSpan={3} sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No Data Found
                  </Typography>
                  </TableCol>
                </TableRow>
              ): ( 
                  displayOpenOrders.map((order: any, idx: number) => (
                    <TableRow key={order.orderNumber || idx} hover>
                      <TableCol onClick={(e) => navigate('/order-management-system/check-order-status/order/' + order.orderNumber)} style={{ cursor: 'pointer', color: '#c3183c', textDecoration: 'underline' }}>
                        {order.orderNumber}
                      </TableCol>
                      <TableCol>{order.customerPo}</TableCol>
                      <TableCol>{moment(order.orderDate).format('MM/DD/YYYY')}</TableCol>
                    </TableRow>
                  ))
                )
              }
            </TableBody>
          </Table>
        </TableContainer>


        <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0, mt: 5 }}>
          <Table size="small" aria-label="inventory list table">
            <TableHead sx={{ backgroundColor: "#c3183c"}}>
              <TableRow>
                <TableCell colSpan={8}  sx={{ color: 'white' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Closed Orders
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      Results: {sortedCloseOrders.length}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
              <TableRow>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleCloseSort('orderNumber')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Sales Order #
                    <Tooltip title="Click to sort">{getSortIcon(closeSort, 'orderNumber')}</Tooltip>
                  </Box>
                </TableCol>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleCloseSort('customerPo')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Purchase Order #
                    <Tooltip title="Click to sort">{getSortIcon(closeSort, 'customerPo')}</Tooltip>
                  </Box>
                </TableCol>
                <TableCol
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleCloseSort('orderDate')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Order Date
                    <Tooltip title="Click to sort">{getSortIcon(closeSort, 'orderDate')}</Tooltip>
                  </Box>
                </TableCol>
              </TableRow>
            </TableHead>
            <TableBody>
              {isCloseLoading ? (
                <TableRow>
                  <TableCol colSpan={3}>
                    <Skeleton width="38%" />
                    <Skeleton width="100%" />
                    <Skeleton width="100%" />
                    <Skeleton width="61%" />
                  </TableCol>
                </TableRow>
              ) : hasCloseFetched && displayCloseOrders.length === 0 ? (
                <TableRow>
                  <TableCol colSpan={3} sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No Data Found
                  </Typography>
                  </TableCol>
                </TableRow>
              ): ( 
                  displayCloseOrders.map((order: any, idx: number) => (
                    <TableRow key={order.orderNumber || idx} hover>
                      <TableCol onClick={(e) => navigate('/order-management-system/check-order-status/order/' + order.orderNumber)} style={{ cursor: 'pointer', color: '#c3183c', textDecoration: 'underline' }}>
                        {order.orderNumber}
                      </TableCol>
                      <TableCol>{order.customerPo}</TableCol>
                      <TableCol>{moment(order.orderDate).format('MM/DD/YYYY')}</TableCol>
                    </TableRow>
                  ))
                )
              }
            </TableBody>
          </Table>
        </TableContainer>


        <Box sx={{ mt: 3, textAlign: 'left' }}>
          <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important' }} onClick={() => {
            queryClient.clear();
            navigate(-1)
          }}>
            BACK
          </Button>
        </Box>
      </Box>
    </DashboardLayout>
  )
}

export default CheckOrderStatusCustomerOrderDetails